import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Home, Users, PartyPopper, Download, Mail, CalendarDays, Info } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { fr as frLocale } from 'date-fns/locale/fr';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';

const RESERVATIONS = [
  { date: new Date(2026, 4, 2), label: 'Interclubs', detail: 'Buvette occupée pour les Interclubs', type: 'interclub' },
  { date: new Date(2026, 4, 8), label: 'AG de la CCT', detail: 'Assemblée générale de la CCT, dès 18h00', type: 'reserved' },
  { date: new Date(2026, 4, 9), label: 'Interclubs', detail: 'Buvette occupée pour les Interclubs', type: 'interclub' },
  { date: new Date(2026, 4, 30), label: 'Apéro soutien Melina', detail: 'Apéro soutien Melina', type: 'reserved' },
  { date: new Date(2026, 5, 7), label: 'Interclubs 3L 35+', detail: 'Buvette occupée pour les Interclubs 3L 35+', type: 'interclub' },
  { date: new Date(2026, 5, 15), label: 'Interclubs 15&U Boys Ligue B', detail: 'Interclubs 15&U Boys Ligue B à 17h15', type: 'interclub' },
  { date: new Date(2026, 5, 17), label: 'Amicale de la Broye', detail: 'Amicale de la Broye', type: 'reserved' },
  { date: new Date(2026, 5, 10), label: 'Interclubs 15&U Girls Ligue B', detail: 'Buvette occupée pour les Interclubs 15&U Girls Ligue B', type: 'interclub' },
  { date: new Date(2026, 5, 13), label: 'Interclubs Juniors', detail: 'Interclubs 15&U Girls Ligue B à 9h / Interclubs 15&U Boys Ligue B à 14h', type: 'interclub' },
  { date: new Date(2026, 5, 14), label: 'Interclubs Kids 10&U', detail: 'Buvette occupée pour les Interclubs Kids 10&U', type: 'interclub' },
  { date: new Date(2026, 5, 21), label: 'Assemblée annuelle du théâtre du vieux pressoir', detail: 'Assemblée annuelle du théâtre du vieux pressoir', type: 'reserved' },
  { date: new Date(2026, 5, 27), label: 'Anniversaire (privé)', detail: 'Réservée toute la journée pour un anniversaire', type: 'reserved' },
  { date: new Date(2026, 9, 3), label: 'Réservation privée', detail: 'Réservée toute la journée', type: 'reserved' },
  { date: new Date(2026, 11, 25), label: 'Réservation privée', detail: 'Réservée toute la journée', type: 'reserved' },
  { date: new Date(2026, 11, 26), label: 'Réservation privée', detail: 'Réservée toute la journée', type: 'reserved' },
];

function ReservationCalendar() {
  const [month, setMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const reservationMap = useMemo(() => {
    const map = {};
    RESERVATIONS.forEach(r => {
      const key = `${r.date.getFullYear()}-${r.date.getMonth()}-${r.date.getDate()}`;
      map[key] = r;
    });
    return map;
  }, []);

  const interclubDates = RESERVATIONS.filter(r => r.type === 'interclub').map(r => r.date);
  const reservedDates = RESERVATIONS.filter(r => r.type === 'reserved').map(r => r.date);

  const getReservation = (date) => {
    if (!date) return null;
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    return reservationMap[key] || null;
  };

  const selectedReservation = getReservation(selectedDate);

  const formatDate = (date) => {
    if (!date) return '';
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  return (
    <div data-testid="reservation-calendar">
      <div className="flex items-center gap-3 mb-6">
        <CalendarDays className="w-8 h-8 text-blue-600" />
        <h2 className="text-3xl font-bold text-gray-900">Disponibilité de la buvette</h2>
      </div>

      <div className="grid md:grid-cols-[auto_1fr] gap-8 items-start">
        {/* Calendar */}
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
          <style>{`
            .reservation-calendar .rdp { --rdp-cell-size: 44px; }
            .reservation-calendar .rdp-month { width: 100%; }
            .reservation-calendar .rdp-table { width: 100%; }
            .reservation-calendar .rdp-head_cell { width: 44px; font-size: 0.85rem; color: #6b7280; }
            .reservation-calendar .rdp-cell { width: 44px; height: 44px; }
            .reservation-calendar .rdp-day { width: 42px; height: 42px; border-radius: 10px; font-size: 0.95rem; transition: all 0.15s; }
            .reservation-calendar .rdp-caption_label { font-size: 1.15rem; font-weight: 700; text-transform: capitalize; }
            .reservation-calendar .rdp-day_today { font-weight: 700; border: 2px solid #3b82f6; }
            .reservation-calendar .day-interclub { background-color: #fed7aa !important; color: #9a3412 !important; font-weight: 600 !important; }
            .reservation-calendar .day-interclub:hover { background-color: #fdba74 !important; }
            .reservation-calendar .day-reserved { background-color: #fecaca !important; color: #991b1b !important; font-weight: 600 !important; }
            .reservation-calendar .day-reserved:hover { background-color: #fca5a5 !important; }
            .reservation-calendar .rdp-day_selected:not(.day-interclub):not(.day-reserved) { background-color: #3b82f6 !important; color: white !important; }
          `}</style>
          <div className="reservation-calendar">
            <DayPickerCalendar
              month={month}
              onMonthChange={setMonth}
              selected={selectedDate}
              onDayClick={(day) => setSelectedDate(day)}
              interclubDates={interclubDates}
              reservedDates={reservedDates}
            />
          </div>

          {/* Légende */}
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-gray-100 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-orange-200 border border-orange-300"></span>
              <span className="text-gray-600">Interclubs</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-red-200 border border-red-300"></span>
              <span className="text-gray-600">Réservée</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-green-100 border border-green-300"></span>
              <span className="text-gray-600">Libre</span>
            </div>
          </div>
        </div>

        {/* Detail panel */}
        <div className="bg-white rounded-2xl shadow-xl p-6 min-h-[200px]">
          {selectedDate ? (
            <div>
              <p className="text-sm text-gray-500 mb-1">Date sélectionnée</p>
              <p className="text-xl font-bold text-gray-900 mb-4">{formatDate(selectedDate)}</p>
              {selectedReservation ? (
                <div className={`rounded-xl p-5 ${selectedReservation.type === 'reserved' ? 'bg-red-50 border border-red-200' : 'bg-orange-50 border border-orange-200'}`}>
                  <span className={`inline-block text-sm font-semibold px-3 py-1 rounded-full mb-3 ${selectedReservation.type === 'reserved' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                    {selectedReservation.type === 'reserved' ? 'Réservée' : 'Occupée - Interclubs'}
                  </span>
                  <p className="font-semibold text-gray-900 text-lg">{selectedReservation.label}</p>
                  <p className="text-gray-600 mt-1">{selectedReservation.detail}</p>
                </div>
              ) : (
                <div className="rounded-xl p-5 bg-green-50 border border-green-200">
                  <span className="inline-block text-sm font-semibold px-3 py-1 rounded-full mb-3 bg-green-100 text-green-700">Libre</span>
                  <p className="font-semibold text-gray-900 text-lg">Aucune réservation</p>
                  <p className="text-gray-600 mt-1">La buvette est disponible à la location pour cette date.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <Info className="w-12 h-12 text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg font-medium">Sélectionnez une date</p>
              <p className="text-gray-400 text-sm mt-1">Cliquez sur un jour du calendrier pour voir sa disponibilité</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DayPickerCalendar({ month, onMonthChange, selected, onDayClick, interclubDates, reservedDates }) {
  const modifiers = {
    interclub: interclubDates,
    reserved: reservedDates,
  };

  const modifiersClassNames = {
    interclub: 'day-interclub',
    reserved: 'day-reserved',
  };

  return (
    <DayPicker
      mode="single"
      locale={frLocale}
      month={month}
      onMonthChange={onMonthChange}
      selected={selected}
      onSelect={onDayClick}
      modifiers={modifiers}
      modifiersClassNames={modifiersClassNames}
      showOutsideDays={false}
      fromMonth={new Date()}
      toMonth={new Date(2026, 11, 31)}
    />
  );
}

const LocationClubhousePage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Header scrollY={100} />
      
      <div className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-green-600 hover:text-green-700 mb-8 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour à l'accueil
          </Link>
          
          <div className="mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Location du club-house
            </h1>
            <p className="text-xl text-gray-600">
              La Buvette du Tennis à Cheyres, votre lieu idéal pour vos événements
            </p>
          </div>

          {/* Photo Gallery */}
          <div className="mb-16 space-y-6">
            {/* Main large photo */}
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_tennis-cheyres/artifacts/i6c5c7bs_Buvette2.png"
                alt="Buvette du Tennis - Vue d'ensemble"
                className="w-full h-96 object-cover"
              />
            </div>
            
            {/* Two smaller photos side by side */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tennis-cheyres/artifacts/0ygwejb8_Buvette1.png"
                  alt="Buvette du Tennis - Cuisine équipée"
                  className="w-full h-72 object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tennis-cheyres/artifacts/gajw741p_TC%20Cheyres.jpeg"
                  alt="Club-house TC Cheyres-Châbles"
                  className="w-full h-72 object-cover"
                />
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-10 rounded-3xl mb-12">
            <div className="flex items-start gap-4 mb-6">
              <Home className="w-12 h-12 text-green-600 flex-shrink-0" />
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">La Buvette du Tennis à Cheyres</h2>
                <p className="text-gray-700 leading-relaxed text-lg mb-6">
                  Le tennis-club peut mettre à disposition la buvette du Club House. 
                  Rénovée en 2019, c'est le lieu idéal pour les réunions de comité, les fêtes de famille, 
                  les anniversaires d'enfants ou tout autre événement.
                </p>
              </div>
            </div>
          </div>

          {/* Usage Examples */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
              <Users className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Réunions professionnelles</h3>
              <p className="text-gray-700 leading-relaxed">
                Réunions de comité, séminaires d'entreprise ou rencontres associatives
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
              <Home className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Fêtes de famille</h3>
              <p className="text-gray-700 leading-relaxed">
                Célébrations familiales, repas de famille dans un cadre chaleureux
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
              <PartyPopper className="w-12 h-12 text-orange-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Anniversaires d'enfants</h3>
              <p className="text-gray-700 leading-relaxed">
                Espace parfait pour organiser une fête d'anniversaire inoubliable
              </p>
            </div>
          </div>

          {/* Calendrier des réservations */}
          <div className="mb-12">
            <ReservationCalendar />
          </div>

          {/* Pricing Table */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Tarifs de location</h2>
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-cyan-400 to-blue-400">
                      <th className="px-6 py-4 text-left font-bold text-gray-900"></th>
                      <th className="px-6 py-4 text-center font-bold text-gray-900">Citoyen</th>
                      <th className="px-6 py-4 text-center font-bold text-gray-900">Non-citoyen</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200 hover:bg-blue-50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-gray-900">
                        Apéritifs / repas de famille
                      </td>
                      <td className="px-6 py-4 text-center font-bold text-gray-900">Fr. 150.-</td>
                      <td className="px-6 py-4 text-center font-bold text-gray-900">Fr. 200.-</td>
                    </tr>
                    <tr className="border-b border-gray-200 hover:bg-blue-50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-gray-900">
                        Banquet / mariage
                      </td>
                      <td className="px-6 py-4 text-center font-bold text-gray-900">Fr. 150.-</td>
                      <td className="px-6 py-4 text-center font-bold text-gray-900">Fr. 250.-</td>
                    </tr>
                    <tr className="hover:bg-blue-50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-gray-900">
                        Réunion / assemblée
                      </td>
                      <td className="px-6 py-4 text-center font-bold text-gray-900">Fr. 100.-</td>
                      <td className="px-6 py-4 text-center font-bold text-gray-900">Fr. 200.-</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Rental Information */}
          <div className="bg-gray-900 text-white p-12 rounded-3xl">
            <h2 className="text-3xl font-bold mb-6">Comment réserver ?</h2>
            <p className="text-gray-300 mb-8 text-lg">
              Pour réserver la buvette à titre privé, téléchargez le formulaire de location, 
              complétez-le et renvoyez-le par email au club.
            </p>
            
            <div className="space-y-4">
              <a 
                href="https://customer-assets.emergentagent.com/job_tennis-cheyres/artifacts/0k7d6tfw_Formulaire%20de%20Location.pdf"
                download
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-semibold transition-all hover:scale-105"
              >
                <Download className="w-5 h-5" />
                Télécharger le formulaire de location
              </a>
              
              <div className="flex items-center gap-3 text-gray-300">
                <Mail className="w-5 h-5" />
                <span>Envoyer le formulaire complété à : </span>
                <a 
                  href="mailto:tccheyres@gmail.com" 
                  className="text-green-400 hover:text-green-300 font-semibold underline"
                >
                  tccheyres@gmail.com
                </a>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-white/20">
              <p className="text-gray-400 mb-4">Des questions sur la location ?</p>
              <button 
                onClick={() => setIsContactModalOpen(true)}
                className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-semibold transition-all hover:scale-105"
              >
                Nous contacter
              </button>
            </div>
          </div>
        </div>
      </div>

      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />

      <Footer />
    </div>
  );
};

export default LocationClubhousePage;
